import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

export type Theme = "light" | "dark";
type ThemeValue = { theme: Theme; toggle(): void };
const ThemeContext = createContext<ThemeValue | null>(null);

function initialTheme(): Theme {
  const saved = localStorage.getItem("agriflow.theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem("agriflow.theme", theme);
  }, [theme]);
  const value = useMemo(() => ({ theme, toggle: () => setTheme(current => current === "dark" ? "light" : "dark") }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("ThemeProvider is missing");
  return value;
}
